import React, { FormEvent, useEffect, useRef, useState } from 'react'
import { CounterProps } from './Counter.types'
import Icon from '../Icon'
import { CounterWrapper, InputCounter } from './styled'
import colors from '../../constants/colors'
import { numberInputChecking } from '../../utils/global'
import Box from '../Box'

const Counter: React.FC<CounterProps> = ({
  max,
  editable = false,
  placeholder = '0',
  onIncrease,
  onDecrease,
  onChange,
  minInput,
  maxInput,
  maxLength,
  min = 0,
  count = 0,
  size = 'small',
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isFocused, setIsFocused] = useState(false)
  const [value, setValue] = useState<number>(
    numberInputChecking(count.toString(), { min, max }).parsedValue,
  )

  useEffect(() => {
    if (!count) return

    setValue(numberInputChecking(count.toString(), { min, max }).parsedValue)
  }, [count])

  const isSmall = size === 'small'
  const isMinimum = value <= min
  const isMaximum = value >= max

  const onIncreaseButtonClicked = () => {
    setValue((prev) => prev + 1)
    // istanbul ignore next
    onIncrease?.(value + 1)
    onChange(value + 1)
  }

  const onDecreaseButtonClicked = () => {
    setValue((prev) => prev - 1)
    // istanbul ignore next
    onDecrease?.(value - 1)
    onChange(value - 1)
  }

  const getInputValue = (event: FormEvent<HTMLInputElement>) => {
    return Number(event.currentTarget.value.replaceAll('.', '') || '0')
  }

  const onChangeInput: React.FormEventHandler<HTMLInputElement> = (
    event: FormEvent<HTMLInputElement>,
  ) => {
    const value = getInputValue(event)

    if (!isNaN(value)) {
      const parsedValue = numberInputChecking(value.toString(), {
        min: minInput ?? min,
        max: maxInput ?? max,
      }).parsedValue
      setValue(parsedValue)
      onChange(parsedValue)
    }
  }

  const onFocus: React.FormEventHandler<HTMLInputElement> = () => {
    setIsFocused(true)
  }

  const onBlur: React.FormEventHandler<HTMLInputElement> = (event: FormEvent<HTMLInputElement>) => {
    setIsFocused(false)

    if (min || max) {
      const value = getInputValue(event)
      const parsedValue = numberInputChecking(value.toString(), { min, max }).parsedValue
      setValue(parsedValue)
      onChange(parsedValue)
    }
  }

  const formattedInputValue = numberInputChecking(value.toString(), {})?.formattedValue || ''

  return (
    <CounterWrapper
      border={`solid 1px ${isFocused ? colors.blue50 : colors.black10}`}
      borderRadius='4px'
      alignItems='center'
      justifyContent='center'
      size={size}
    >
      <Box
        as='button'
        bg='white'
        border='none'
        padding={0}
        onClick={!isMinimum ? onDecreaseButtonClicked : undefined}
        cursor={isMinimum ? 'not-allowed' : 'pointer'}
        aria-label='decrease-button'
      >
        <Icon
          name='Minus-outline'
          size={isSmall ? 16 : 24}
          color={isMinimum ? 'black25' : 'blue50'}
        />
      </Box>
      <InputCounter
        ref={inputRef}
        data-testid='input-counter'
        value={formattedInputValue}
        placeholder={placeholder}
        disabled={!editable}
        size={formattedInputValue.length}
        onChange={onChangeInput}
        onFocus={onFocus}
        onBlur={onBlur}
        maxLength={maxLength}
      />
      <Box
        as='button'
        bg='white'
        border='none'
        padding={0}
        onClick={!isMaximum ? onIncreaseButtonClicked : undefined}
        cursor={max && isMaximum ? 'not-allowed' : 'pointer'}
        aria-label='increase-button'
      >
        <Icon
          name='Plus-outline'
          size={isSmall ? 16 : 24}
          color={max && isMaximum ? 'black25' : 'blue50'}
        />
      </Box>
    </CounterWrapper>
  )
}

export default Counter
